import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createNotification } from "../../utils/notificationManager";
import MyLoader from "../Common/MyLoader";
import CancelModal from "./CancelModal";
import CurrentPlan from "./CurrentPlan";
import NoPlan from "./NoPlan";
import { initializePaddle } from "./Paddle";
import PausedPlan from "./PausedPlan";
import UpgradePlanModal from "./UpgradePlanModal";
import { useLocation } from "react-router-dom";
import {
  getPaddleBillingClient,
  getPaddleBillingConfig,
  subscribeToPaddleBillingEvents,
} from "../../utils/paddleBilling";
import { getBillingPricePreviewLabel } from "./billingCatalog";

const BILLING_PORTAL_UNAVAILABLE_MESSAGE =
  "Billing portal is not available for this subscription.";

const MyPlan = (props) => {
  const [Modal, setModal] = useState({
    cancelModal: false,
    activateModal: false,
    upgradeModal: false,
  });

  const [loader, setLoader] = useState(false);
  const [isPaddleInitialized, setIsPaddleInitialized] = useState(false);
  const [pendingBillingMutation, setPendingBillingMutation] = useState(false);
  const visibilityStateRef = useRef(document.visibilityState);
  const location = useLocation();
  const { state } = location;
  const {
    myPlan,
    isLoading,
    auth,
    manageSubscription,
    submitFeedback,
    createBillingCheckout,
    applyBillingPlanChange,
    createBillingPortalSession,
    billingPlanChangeLoading,
    billingMutationError,
    billingPortalSessionLoading,
    getSubscriptionPlan,
    getBillingPlans,
    billingPlans,
    billingPlansLoading,
    billingPlansError,
    cancelPlan,
    upgradePlan,
  } = props;
  const isBilling = myPlan?.is_billing === true;
  const planActions = myPlan?.actions || {};
  const planIdentity = isBilling
    ? myPlan?.payment_information?.price_id || myPlan?.paddle_plan_id
    : myPlan?.paddle_plan_id;
  const billingPriceIds = useMemo(
    () => (billingPlans || []).map((plan) => plan?.price_id).filter(Boolean),
    [billingPlans]
  );
  const billingConfig = getPaddleBillingConfig();
  const billingPricePreviewCacheKey = useMemo(() => {
    return `${billingConfig.billingEnv}:${billingPriceIds.join("|")}`;
  }, [billingConfig.billingEnv, billingPriceIds]);
  const billingPricePreviewRequestKeyRef = useRef("");
  const [billingPriceLabels, setBillingPriceLabels] = useState({});
  const [billingPricePreviewLoading, setBillingPricePreviewLoading] =
    useState(false);
  const billingPricePreviewPending =
    billingPriceIds.length > 0 &&
    !billingPlansLoading &&
    !billingPlansError &&
    billingConfig.isBillingConfigured &&
    billingPricePreviewRequestKeyRef.current !== billingPricePreviewCacheKey;
  const billingPricePreviewIsLoading =
    billingPricePreviewLoading || billingPricePreviewPending;

  const initiateAfterCheckoutLoader = useCallback((eventInfo) => {
    if (eventInfo?.override) {
      const splittedURL = eventInfo?.override?.split("/");
      // Checking if update override url ends with cancel or not, if yes then update store state
      if (splittedURL[splittedURL.length - 1] === "cancel") {
        cancelPlan();
      }
    }

    // Set Loader true for 5 seconds
    setLoader(true);
    setTimeout(() => {
      setLoader(false);
      getSubscriptionPlan(true); // Call getSubscriptionPlan after 5 seconds
    }, 5000);
  }, [cancelPlan, getSubscriptionPlan]);

  useEffect(() => {
    if (!isBilling && !isPaddleInitialized) {
      initializePaddle(initiateAfterCheckoutLoader).then((initialized) => {
        if (initialized) {
          setIsPaddleInitialized(true);
        }
      });
    }
  }, [isBilling, isPaddleInitialized, initiateAfterCheckoutLoader]);

  useEffect(() => {
    return subscribeToPaddleBillingEvents((event) => {
      if (event?.name !== "checkout.completed") {
        return;
      }

      setLoader(true);
      getSubscriptionPlan({ refetch: true, silent: true });

      setTimeout(() => {
        setLoader(false);
      }, 5000);
    });
  }, [getSubscriptionPlan]);

  useEffect(() => {
    if(state?.subs_modal){
      setLoader(true);
      setTimeout(() => {
        setLoader(false);
        setModal((currentModal) => ({
          ...currentModal,
          upgradeModal: true,
        }));
      }, 5000);
    }
  }, [state?.subs_modal]);

  useEffect(() => {
    getSubscriptionPlan();
  }, [getSubscriptionPlan]);

  useEffect(() => {
    getBillingPlans();
  }, [getBillingPlans]);

  useEffect(() => {
    let isCancelled = false;
    if (
      billingPlansLoading ||
      billingPlansError ||
      billingPriceIds.length === 0 ||
      !billingConfig.isBillingConfigured ||
      billingPricePreviewRequestKeyRef.current === billingPricePreviewCacheKey
    ) {
      if (billingPlansError || billingPriceIds.length === 0) {
        setBillingPriceLabels({});
      }
      setBillingPricePreviewLoading(false);
      return () => {
        isCancelled = true;
      };
    }

    setBillingPricePreviewLoading(true);
    setBillingPriceLabels({});

    async function loadBillingPricePreview() {
      try {
        const paddle = await getPaddleBillingClient();
        const preview = await paddle.PricePreview({
          items: billingPriceIds.map((priceId) => ({
            priceId,
            quantity: 1,
          })),
        });

        if (isCancelled) {
          return;
        }

        const lineItems =
          preview?.data?.details?.lineItems ||
          preview?.data?.details?.line_items ||
          [];
        const nextLabels = {};

        lineItems.forEach((lineItem) => {
          const priceId = lineItem?.price?.id;
          const label = getBillingPricePreviewLabel(lineItem);

          if (priceId && label) {
            nextLabels[priceId] = label;
          }
        });

        billingPricePreviewRequestKeyRef.current = billingPricePreviewCacheKey;
        setBillingPriceLabels(nextLabels);
        setBillingPricePreviewLoading(false);
      } catch (error) {
        if (!isCancelled) {
          billingPricePreviewRequestKeyRef.current = billingPricePreviewCacheKey;
          setBillingPriceLabels({});
          setBillingPricePreviewLoading(false);
        }
      }
    }

    loadBillingPricePreview();

    return () => {
      isCancelled = true;
    };
  }, [
    billingConfig.isBillingConfigured,
    billingPlansError,
    billingPlansLoading,
    billingPriceIds,
    billingPricePreviewCacheKey,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const previousVisibilityState = visibilityStateRef.current;
      const nextVisibilityState = document.visibilityState;

      visibilityStateRef.current = nextVisibilityState;

      if (
        previousVisibilityState === "hidden" &&
        nextVisibilityState === "visible"
      ) {
        getSubscriptionPlan({ refetch: true, silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [getSubscriptionPlan]);

  const initiatePlanUpgrade = (data) => {
    if (String(data?.plan_id) === String(myPlan?.paddle_plan_id)) {
      createNotification("warning", "You already have this plan", 2000);
      return;
    }
    upgradePlan(data);
  };

  const initiateBillingPlanChange = (data) => {
    setPendingBillingMutation(true);
    applyBillingPlanChange(data);
  };

  const initiateSubscriptionManager = (data) => {
    if (isBilling && data?.portal) {
      createBillingPortalSession({});
      return;
    }

    manageSubscription(data);
    setLoader(true);
    setTimeout(() => {
      setLoader(false);
    }, 5000);
  };

  const openBillingPortal = (subscription = myPlan) => {
    if (!subscription?.is_billing) {
      createNotification(
        "warning",
        BILLING_PORTAL_UNAVAILABLE_MESSAGE,
        3000
      );
      return;
    }

    createBillingPortalSession({});
  };

  useEffect(() => {
    if (!pendingBillingMutation || billingPlanChangeLoading) {
      return;
    }

    if (!billingMutationError) {
      setModal((currentModal) => ({
        ...currentModal,
        cancelModal: false,
        upgradeModal: false,
      }));
    }

    setPendingBillingMutation(false);
  }, [billingMutationError, billingPlanChangeLoading, pendingBillingMutation]);

  return (
    <div
      style={{
        padding: "20px 5%",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <MyLoader open={isLoading || loader} />
      <CancelModal
        cancelURL={myPlan?.cancel_url}
        modal={Modal}
        setModal={setModal}
        managePauseSubscription={initiateSubscriptionManager}
        isPaused={myPlan?.payment_information?.paused_at ? true : false}
        submitFeedback={submitFeedback}
        isLoading={isLoading}
        actions={planActions}
      />
      <UpgradePlanModal
        modal={Modal}
        setModal={setModal}
        planStatus={myPlan?.status}
        planId={planIdentity}
        currentPlanUnitPrice={myPlan?.unit_price}
        currentPlanCurrency={myPlan?.currency}
        currentPlanName={myPlan?.plan_name}
        isBilling={isBilling}
        billingPlans={props.billingPlans}
        billingPlansLoading={props.billingPlansLoading}
        billingPlansError={props.billingPlansError}
        billingPriceLabels={billingPriceLabels}
        billingPricePreviewLoading={billingPricePreviewIsLoading}
        retryBillingPlans={getBillingPlans}
        billingActions={planActions}
        userMail={
          auth?.auth?.user?.emails?.find((item) => item?.default === true)
            ?.email
        }
        initiatePlanUpgrade={initiatePlanUpgrade}
        initiateBillingCheckout={createBillingCheckout}
        billingPlanChangeLoading={billingPlanChangeLoading}
        billingMutationError={billingMutationError}
        confirmBillingPlanChange={initiateBillingPlanChange}
      />
      <div
        style={{
          marginBottom: "20px",
        }}
        className="dashboardHeader"
      >
        <div>
          <span className="homeHead">My Subscription Plan</span>
        </div>
      </div>
      <div className="subscontent">
        {(myPlan?.status === "active" ||
          myPlan?.status === "trialing" ||
          myPlan?.status === "past_due") &&
        myPlan.is_active === true &&
        myPlan.status !== "deleted" ? (
          <CurrentPlan
            planInfo={myPlan}
            modal={Modal}
            setModal={setModal}
            resumeSubscription={initiateSubscriptionManager}
            isBilling={isBilling}
            actions={planActions}
            openBillingPortal={openBillingPortal}
            billingPortalSessionLoading={billingPortalSessionLoading}
          />
        ) : myPlan?.status === "paused" ? (
          <PausedPlan
            planInfo={myPlan}
            manageSubscription={initiateSubscriptionManager}
            isBilling={isBilling}
            actions={planActions}
            openBillingPortal={openBillingPortal}
            billingPortalSessionLoading={billingPortalSessionLoading}
          />
        ) : (
          <NoPlan memberSince={myPlan?.created_at} setModal={setModal} />
        )}
      </div>
    </div>
  );
};

export default MyPlan;
